import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  getCustomers, 
  getProducts, 
  getCoBrandings,
  createDispatch, 
  updateDispatch, 
  getDispatch,
  createCustomer,
  getTransportOptions,
  getCustomerAddresses,
  getCustomerMappingsLookup,
  createCustomerProductMapping,
  updateCustomerProductMapping,
  reverseLookupSupplierCode,
  getNextDispatchNumber,
  checkDuplicatePo,
  lookupFormula,
  learnFormula
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { canEdit, getTodayFormatted } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { SearchableSelectWithAction } from '../components/ui/searchable-select-with-action';
import { DatePicker } from '../components/ui/date-picker';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import DuplicatePoWarningModal from '../components/DuplicatePoWarningModal';
import { 
  Save, 
  Loader2,
  ArrowLeft,
  MapPin,
  AlertTriangle,
  UserPlus,
  Truck,
  Hash,
  CheckCircle2,
  X,
  Package,
} from 'lucide-react';
import { NewCustomerDialog, SupplierCodeDialog } from './sales-order/SalesOrderDialogs';
import { OrderLineItemsTable } from './sales-order/OrderLineItemsTable';
import { SeasonalSplitNotice, SplitCreatedNote } from '../components/phased/SeasonalSplitNotice';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

export default function SalesOrderEntry() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // ── Duplicate-PO warning flow ─────────────────────────────────────
  const [duplicatePoModalOpen, setDuplicatePoModalOpen] = useState(false);
  const [duplicatePoExisting, setDuplicatePoExisting] = useState(null);
  const [acknowledgeDuplicatePo, setAcknowledgeDuplicatePo] = useState(false);
  const [checkingDuplicatePo, setCheckingDuplicatePo] = useState(false);
  // Inline live check (as-you-type) ── separate from the blocking modal flow
  const [inlinePoStatus, setInlinePoStatus] = useState('idle'); // 'idle' | 'checking' | 'duplicate' | 'available'
  const [inlineDuplicateOrder, setInlineDuplicateOrder] = useState(null);
  const [poModalShownForKey, setPoModalShownForKey] = useState(null); // dedupe to avoid re-popping
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [cobrandings, setCobrandings] = useState([]);
  const [transportOptions, setTransportOptions] = useState([]);
  
  // Form state
  const [customerId, setCustomerId] = useState('');
  const [customerData, setCustomerData] = useState(null);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('primary');
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [cobrandingId, setCobrandingId] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate] = useState('');
  const [dateOrderReceived, setDateOrderReceived] = useState(getTodayFormatted());
  const [dispatchDate, setDispatchDate] = useState('');
  const [customerDeliveryDate, setCustomerDeliveryDate] = useState('');
  const [transportAgent, setTransportAgent] = useState('');
  const [numPallets, setNumPallets] = useState('');
  const [notes, setNotes] = useState('');
  const [moqAlert, setMoqAlert] = useState(null); // { text, customerName }
  const [orderNumber, setOrderNumber] = useState('');
  const [status, setStatus] = useState('draft');
  
  // Order Lines
  const [lines, setLines] = useState([createEmptyLine()]);
  
  // New Customer Dialog
  const [newCustomerDialogOpen, setNewCustomerDialogOpen] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  
  // Supplier Product Code state
  const [supplierCodeLookup, setSupplierCodeLookup] = useState({}); // { product_id: supplier_code }
  const [supplierCodeOptions, setSupplierCodeOptions] = useState([]); // [{value: code, label: "CODE - Product (Pkg)", ...}]
  const [addSupplierCodeDialogOpen, setAddSupplierCodeDialogOpen] = useState(false);
  const [supplierCodeLineIndex, setSupplierCodeLineIndex] = useState(null);
  const [newSupplierCode, setNewSupplierCode] = useState('');
  const [savingSupplierCode, setSavingSupplierCode] = useState(false);

  // Quick-edit customer product code state
  const [editCodeDialogOpen, setEditCodeDialogOpen] = useState(false);
  const [editCodeLineIndex, setEditCodeLineIndex] = useState(null);
  const [editCodeValue, setEditCodeValue] = useState('');
  const [editCodeMappingId, setEditCodeMappingId] = useState(null);
  const [savingEditCode, setSavingEditCode] = useState(false);

  // Next order number preview (real-time)
  const [nextNumber, setNextNumber] = useState('');
  const [nextNumberLoading, setNextNumberLoading] = useState(false);

  // Success popup
  const [successPopup, setSuccessPopup] = useState(null); // { number: string } or null

  // Weight unit toggle (lbs mode: user enters lbs, saves as kgs)
  const [weightInLbs, setWeightInLbs] = useState(false);

  function createEmptyLine() {
    return {
      id: (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36)),
      product_id: '',
      product_name: '',
      cobranding_id: '',
      quantity: '',
      cases: '',
      estimated_weight: '',
      notes: '',
      supplier_product_code: '',
      supplier_code_locked: false,
      // Conversion formula state
      formula: null,         // { unit_weight_kg, units_per_case, mapping_id, source }
      formulaOverride: false, // User wants to override auto-fill
      formulaLearning: false, // No formula exists — system will learn from this entry
    };
  }

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Fetch next order number (for new orders only) and poll every 30s
  useEffect(() => {
    if (isEdit) return;
    let active = true;
    const fetchNext = async () => {
      try {
        setNextNumberLoading(true);
        const res = await getNextDispatchNumber();
        if (active) setNextNumber(res.data.next_number);
      } catch { /* ignore */ }
      finally { if (active) setNextNumberLoading(false); }
    };
    fetchNext();
    const interval = setInterval(fetchNext, 30000);
    return () => { active = false; clearInterval(interval); };
  }, [isEdit]);

  // Load order when editing - depends on customers being loaded
  useEffect(() => {
    if (isEdit && customers.length > 0) {
      loadOrder();
    }
  }, [id, customers.length]);

  // ── Inline live duplicate-PO check (as the user types) ───────────────
  // Debounces a backend lookup against (customer_id + po_number). Used for
  // showing an inline status badge ("Checking…", "Already used", "Available").
  // A separate handleBlurPoNumber() handler opens the blocking modal only
  // when focus actually leaves the field (i.e. user moves to next column).
  //
  // Performance hardening:
  // - Debounced 350ms so we don't fire on every keystroke
  // - AbortController cancels stale in-flight requests when the user keeps typing
  // - Backend uses an indexed exact-equality lookup on po_number_normalized,
  //   so even customers with thousands of orders return in <10ms
  useEffect(() => {
    const trimmed = (poNumber || '').trim();
    // Reset any in-flight result when inputs are invalid
    if (!customerId || !trimmed) {
      setInlinePoStatus('idle');
      setInlineDuplicateOrder(null);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    setInlinePoStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const res = await checkDuplicatePo(customerId, trimmed, isEdit ? id : undefined, { signal: controller.signal });
        if (cancelled) return;
        if (res.data?.duplicate_found) {
          setInlineDuplicateOrder(res.data.existing_order || null);
          setInlinePoStatus('duplicate');
        } else {
          setInlineDuplicateOrder(null);
          setInlinePoStatus('available');
        }
      } catch (err) {
        // Ignore aborts (expected when user keeps typing) and network blips
        if (!cancelled && err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
          setInlinePoStatus('idle');
          setInlineDuplicateOrder(null);
        }
      }
    }, 350); // debounce
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [poNumber, customerId, isEdit, id]);

  // Reset inline state whenever the user manually acknowledges or changes the PO
  useEffect(() => {
    if (!poNumber || !customerId) setPoModalShownForKey(null);
  }, [poNumber, customerId]);

  // Triggered when focus leaves the PO Number input — if the live check says
  // duplicate, immediately pop the warning modal (only once per PO+customer key).
  const handleBlurPoNumber = () => {
    const trimmed = (poNumber || '').trim();
    if (!trimmed || !customerId) return;
    const key = `${customerId}::${trimmed.toUpperCase()}`;
    if (poModalShownForKey === key) return; // already shown for this combo
    if (inlinePoStatus === 'duplicate' && inlineDuplicateOrder) {
      // Avoid pop-up when editing the very same order we are looking at
      if (isEdit && inlineDuplicateOrder.id === id) return;
      setDuplicatePoExisting(inlineDuplicateOrder);
      setDuplicatePoModalOpen(true);
      setPoModalShownForKey(key);
    }
  };

  const loadInitialData = async () => {
    try {
      // Load each data source independently to prevent one failure from blocking all
      const [customersRes, productsRes, cobrandingsRes, transportRes] = await Promise.allSettled([
        getCustomers(),
        getProducts(),
        getCoBrandings(),
        getTransportOptions(),
      ]);
      
      // Set data from successful responses - default to empty arrays
      setCustomers(customersRes.status === 'fulfilled' ? (customersRes.value.data || []) : []);
      setProducts(productsRes.status === 'fulfilled' ? (productsRes.value.data || []) : []);
      setCobrandings(cobrandingsRes.status === 'fulfilled' ? (cobrandingsRes.value.data || []) : []);
      setTransportOptions(transportRes.status === 'fulfilled' ? (transportRes.value.data || []) : []);
      
      // Log any failures for debugging
      const failures = [];
      if (customersRes.status === 'rejected') failures.push('customers');
      if (productsRes.status === 'rejected') failures.push('products');
      if (cobrandingsRes.status === 'rejected') failures.push('cobrandings');
      
      if (failures.length > 0) {
        console.error('Failed to load:', failures.join(', '));
        // Only show error toast if critical data failed AND we have no data
        if ((customersRes.status === 'rejected' || productsRes.status === 'rejected')) {
          const errorReason = customersRes.reason || productsRes.reason;
          if (errorReason?.response?.status === 401) {
            toast.error('Session expired. Please log in again.');
          } else {
            toast.error('Some data failed to load. Please refresh the page.');
          }
        }
      }
    } catch (error) {
      console.error('loadInitialData error:', error);
      toast.error('Failed to load data');
    }
  };

  const loadOrder = async () => {
    setLoading(true);
    try {
      const response = await getDispatch(id);
      const order = response.data;
      
      setCustomerId(order.customer_id);
      setPoNumber(order.po_number || '');
      setPoDate(order.po_date || '');
      setDateOrderReceived(order.date_order_received || order.dispatch_date || '');
      setDispatchDate(order.dispatch_date || '');
      setCustomerDeliveryDate(order.customer_delivery_date || '');
      setTransportAgent(order.vehicle_info || '');
      setNumPallets(order.num_pallets?.toString() || '');
      setNotes(order.notes || '');
      setOrderNumber(order.dispatch_number);
      setStatus(order.status);
      setCobrandingId(order.cobranding_id || '');
      
      // Load customer data
      const customer = customers.find(c => c.id === order.customer_id);
      setCustomerData(customer);
      
      // Load customer addresses and supplier codes in parallel
      if (order.customer_id) {
        const [addressRes, mappingsRes] = await Promise.allSettled([
          getCustomerAddresses(order.customer_id),
          getCustomerMappingsLookup(order.customer_id)
        ]);
        
        // Handle addresses
        if (addressRes.status === 'fulfilled') {
          const allAddresses = addressRes.value.data.all_addresses || [];
          setCustomerAddresses(allAddresses);
          
          // Try to restore saved address selection
          const savedAddrId = order.delivery_address_id;
          if (savedAddrId) {
            const savedAddr = allAddresses.find(a => a.id === savedAddrId);
            if (savedAddr) {
              setSelectedAddressId(savedAddrId);
              setSelectedAddress(savedAddr);
            } else {
              const defaultAddr = allAddresses.find(a => a.is_default) || allAddresses[0];
              if (defaultAddr) {
                setSelectedAddressId(defaultAddr.id);
                setSelectedAddress(defaultAddr);
              }
            }
          } else {
            const defaultAddr = allAddresses.find(a => a.is_default) || allAddresses[0];
            if (defaultAddr) {
              setSelectedAddressId(defaultAddr.id);
              setSelectedAddress(defaultAddr);
            }
          }
        } else if (customer) {
          // Fall back to customer primary address
          const primaryAddr = {
            id: 'primary',
            label: 'Primary Address',
            address_line1: customer.delivery_address_line1,
            address_line2: customer.delivery_address_line2,
            city: customer.delivery_city,
            county: customer.delivery_county,
            postcode: customer.delivery_postcode,
            country: customer.delivery_country || 'Ireland',
            contact_name: customer.contact_name,
            contact_phone: customer.contact_phone,
            delivery_instructions: customer.default_delivery_instructions,
            is_primary: true
          };
          setCustomerAddresses([primaryAddr]);
          setSelectedAddressId('primary');
          setSelectedAddress(primaryAddr);
        }
        
        // Handle supplier product code mappings
        if (mappingsRes.status === 'fulfilled') {
          const lookup = mappingsRes.value.data.lookup || {};
          const mappings = mappingsRes.value.data.mappings || [];
          setSupplierCodeLookup(lookup);
          
          // Build supplier code dropdown options
          const options = mappings.map(m => {
            const product = products.find(p => p.id === m.internal_product_id);
            const cobranding = cobrandings.find(c => c.id === m.cobranding_id);
            const productName = product?.name || 'Unknown Product';
            const pkgLabel = cobranding ? ` (${cobranding.name})` : '';
            return {
              value: m.supplier_product_code,
              displayValue: m.supplier_product_code,
              label: `${m.supplier_product_code} - ${productName}${pkgLabel}`,
              mapping_id: m.id,
              product_id: m.internal_product_id,
              product_name: productName,
              cobranding_id: m.cobranding_id || '',
              cobranding_name: cobranding?.name || ''
            };
          });
          setSupplierCodeOptions(options);
        }
      }
      
      // Map lines (use line.id from backend, fallback to line_id)
      if (order.lines && order.lines.length > 0) {
        setLines(order.lines.map(line => ({
          id: line.id || line.line_id || (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36)),
          product_id: line.product_id || '',
          product_name: line.product_name || '',
          cobranding_id: line.cobranding_id || '',
          quantity: line.quantity?.toString() || '',
          cases: line.cases?.toString() || line.calculated_cases?.toString() || '',
          estimated_weight: line.total_weight?.toString() || line.estimated_weight?.toString() || '',
          notes: line.notes || '',
          supplier_product_code: line.supplier_product_code || '',
        })));
      }
    } catch (error) {
      toast.error('Failed to load order');
      navigate('/planner');
    } finally {
      setLoading(false);
    }
  };

  // Customer selection handler
  const handleCustomerChange = async (value) => {
    setCustomerId(value);
    setAcknowledgeDuplicatePo(false);
    const customer = customers.find(c => c.id === value);
    setCustomerData(customer);
    
    // Reset address selection
    setCustomerAddresses([]);
    setSelectedAddressId('primary');
    setSelectedAddress(null);
    
    // Reset supplier code lookup and options
    setSupplierCodeLookup({});
    setSupplierCodeOptions([]);
    
    // Auto-populate transport agent from customer default (still editable)
    if (customer?.default_transport_agent && customer.default_transport_agent !== 'none_selected') {
      setTransportAgent(customer.default_transport_agent);
    }
    
    // Show MOQ alert banner if set
    if (customer?.moq_alert_text) {
      setMoqAlert({ text: customer.moq_alert_text, customerName: customer.name });
    } else {
      setMoqAlert(null);
    }
    
    // Load customer addresses and supplier product codes
    if (value) {
      try {
        const [addressRes, mappingsRes] = await Promise.allSettled([
          getCustomerAddresses(value),
          getCustomerMappingsLookup(value)
        ]);
        
        // Handle addresses
        if (addressRes.status === 'fulfilled') {
          const allAddresses = addressRes.value.data.all_addresses || [];
          setCustomerAddresses(allAddresses);
          
          const defaultAddr = allAddresses.find(a => a.is_default) || allAddresses[0];
          if (defaultAddr) {
            setSelectedAddressId(defaultAddr.id);
            setSelectedAddress(defaultAddr);
          }
        } else if (customer) {
          // Fall back to customer primary address
          const primaryAddr = {
            id: 'primary',
            label: 'Primary Address',
            address_line1: customer.delivery_address_line1,
            address_line2: customer.delivery_address_line2,
            city: customer.delivery_city,
            county: customer.delivery_county,
            postcode: customer.delivery_postcode,
            country: customer.delivery_country || 'Ireland',
            contact_name: customer.contact_name,
            contact_phone: customer.contact_phone,
            delivery_instructions: customer.default_delivery_instructions,
            is_primary: true
          };
          setCustomerAddresses([primaryAddr]);
          setSelectedAddressId('primary');
          setSelectedAddress(primaryAddr);
        }
        
        // Handle supplier product code mappings
        if (mappingsRes.status === 'fulfilled') {
          const lookup = mappingsRes.value.data.lookup || {};
          const mappings = mappingsRes.value.data.mappings || [];
          setSupplierCodeLookup(lookup);
          
          // Build supplier code dropdown options
          // value = the code itself, label = full searchable text, displayValue = just the code for display
          const options = mappings.map(m => {
            const product = products.find(p => p.id === m.internal_product_id);
            const cobranding = cobrandings.find(c => c.id === m.cobranding_id);
            const productName = product?.name || 'Unknown Product';
            const pkgLabel = cobranding ? ` (${cobranding.name})` : '';
            return {
              value: m.supplier_product_code,
              displayValue: m.supplier_product_code, // Show only code when selected
              label: `${m.supplier_product_code} - ${productName}${pkgLabel}`, // Full text for searching
              mapping_id: m.id,
              product_id: m.internal_product_id,
              product_name: productName,
              cobranding_id: m.cobranding_id || '',
              cobranding_name: cobranding?.name || ''
            };
          });
          setSupplierCodeOptions(options);
          
          // Update existing lines with supplier codes (considering packaging)
          setLines(prevLines => prevLines.map(line => {
            if (line.product_id) {
              // First try to find with packaging combo
              const comboKey = line.cobranding_id ? `${line.product_id}:${line.cobranding_id}` : null;
              if (comboKey && lookup[comboKey]) {
                const mapping = lookup[comboKey];
                return { ...line, supplier_product_code: mapping.supplier_product_code || mapping };
              }
              // Fall back to product-only lookup
              if (lookup[line.product_id]) {
                const mapping = lookup[line.product_id];
                return { ...line, supplier_product_code: mapping.supplier_product_code || mapping };
              }
            }
            return { ...line, supplier_product_code: '' };
          }));
        }
      } catch (error) {
        console.error('Failed to load customer data', error);
      }
    }
  };

  // Handle address selection change
  const handleAddressChange = (addressId) => {
    setSelectedAddressId(addressId);
    const addr = customerAddresses.find(a => a.id === addressId);
    setSelectedAddress(addr);
  };

  // Line management
  const addLine = () => {
    setLines([...lines, createEmptyLine()]);
  };

  const removeLine = (index) => {
    if (lines.length === 1) return;
    setLines(lines.filter((_, i) => i !== index));
  };

  const updateLine = async (index, field, value) => {
    const newLines = [...lines];
    const line = { ...newLines[index], [field]: value };

    // ── Auto-fill weight / cases / qty ───────────────────────────
    //
    // Priority for the unit-weight / units-per-case values used in the
    // calculation:
    //   1. Customer-specific conversion formula (lookupLineFormula).
    //   2. Product-level fixed-weight metadata (Product menu →
    //      "Fixed Weight Product" checkbox). This is the fallback for
    //      every fixed-weight retail SKU even if the customer has no
    //      learned formula yet.
    //
    // Variable-weight products (e.g. whole cheeses, where each unit
    // has its own weight) always require manual entry — they fall
    // through both paths.
    const formula = line.formula;
    const hasFormula = !!(formula && formula.unit_weight_kg > 0);
    const isFixedWeightFallback = (
      !hasFormula
      && line.is_fixed_weight
      && parseFloat(line.weight_per_unit) > 0
    );

    let uwkg = 0;
    let upc = 0;
    if (hasFormula) {
      uwkg = formula.unit_weight_kg;
      upc = formula.units_per_case;
    } else if (isFixedWeightFallback) {
      uwkg = parseFloat(line.weight_per_unit) || 0;
      upc = parseFloat(line.units_per_case) || 0;
    }

    if (
      uwkg > 0
      && !line.formulaOverride
      && (field === 'quantity' || field === 'cases' || field === 'estimated_weight')
    ) {
      if (field === 'quantity') {
        const qty = parseFloat(value) || 0;
        if (qty > 0) {
          line.estimated_weight = (qty * uwkg).toFixed(2);
          if (upc > 0) line.cases = Math.ceil(qty / upc).toString();
        }
      } else if (field === 'cases' && upc > 0) {
        const cs = parseFloat(value) || 0;
        if (cs > 0) {
          const qty = cs * upc;
          line.quantity = qty.toString();
          line.estimated_weight = (qty * uwkg).toFixed(2);
        }
      } else if (field === 'estimated_weight') {
        // For fixed-weight products this back-calc is rarely useful
        // (operators always enter quantity, never weight), but we keep
        // it for the customer-formula path so users can type the
        // weighed value and have cases/qty derived.
        const wt = parseFloat(value) || 0;
        if (wt > 0) {
          const qty = Math.round(wt / uwkg);
          line.quantity = qty.toString();
          if (upc > 0) line.cases = Math.ceil(qty / upc).toString();
        }
      }
    }

    newLines[index] = line;
    setLines(newLines);
  };

  // Look up conversion formula for a line's product+customer combo
  const lookupLineFormula = useCallback(async (index, productId, cobrandingId) => {
    if (!customerId || !productId) return;
    try {
      const { data } = await lookupFormula(customerId, productId, cobrandingId || '');
      setLines(prev => {
        const newLines = [...prev];
        if (data.has_formula) {
          newLines[index] = {
            ...newLines[index],
            formula: {
              unit_weight_kg: data.unit_weight_kg,
              units_per_case: data.units_per_case,
              mapping_id: data.mapping_id,
              source: data.formula_source,
            },
            formulaLearning: false,
          };
        } else {
          newLines[index] = {
            ...newLines[index],
            formula: null,
            formulaLearning: true,
          };
        }
        return newLines;
      });
    } catch {
      // Silently fail — formula is optional
    }
  }, [customerId]);

  // Save learned formula from a line after order save
  const saveLearnedFormulas = useCallback(async () => {
    for (const line of lines) {
      if (!line.formulaLearning || line.formulaOverride) continue;
      const qty = parseInt(line.quantity) || 0;
      const cs = parseInt(line.cases) || 0;
      const wt = parseFloat(line.estimated_weight) || 0;
      if (qty > 0 && cs > 0 && wt > 0 && line.product_id) {
        try {
          await learnFormula({
            customer_id: customerId,
            product_id: line.product_id,
            cobranding_id: line.cobranding_id || null,
            supplier_product_code: line.supplier_product_code || null,
            weight_kg: wt,
            units: qty,
            cases: cs,
          });
        } catch {
          // Non-critical
        }
      }
    }
  }, [lines, customerId]);

  // Handle supplier code input change
  const handleSupplierCodeChange = (index, value) => {
    const newLines = [...lines];
    newLines[index] = { ...newLines[index], supplier_product_code: value };
    setLines(newLines);
  };

  // Handle supplier code selection from dropdown - auto-fill product and packaging
  const handleSupplierCodeSelect = (index, supplierCode) => {
    const option = supplierCodeOptions.find(o => o.value === supplierCode);
    if (option) {
      const newLines = [...lines];
      const product = products.find(p => p.id === option.product_id);
      newLines[index] = {
        ...newLines[index],
        supplier_product_code: supplierCode,
        product_id: option.product_id,
        product_name: option.product_name,
        cobranding_id: option.cobranding_id || '',
        supplier_code_locked: true,  // Lock the combination - supplier code was selected first
        // Carry product-level auto-calc metadata onto the line
        is_fixed_weight: !!product?.is_fixed_weight,
        weight_per_unit: product?.weight_per_unit ?? null,
        units_per_case: product?.units_per_case ?? null,
      };
      setLines(newLines);
      toast.success(`Loaded: ${option.product_name}${option.cobranding_name ? ` (${option.cobranding_name})` : ''}`);
      // Lookup conversion formula for this product+customer combo
      lookupLineFormula(index, option.product_id, option.cobranding_id || '');
    } else {
      // Clear if deselected
      const newLines = [...lines];
      newLines[index] = { 
        ...newLines[index], 
        supplier_product_code: '',
        supplier_code_locked: false  // Unlock when cleared
      };
      setLines(newLines);
    }
  };

  // Handle "+ Create New" from supplier code dropdown
  const handleCreateNewSupplierCode = (index, initialCode = '') => {
    setSupplierCodeLineIndex(index);
    setNewSupplierCode(initialCode);
    setAddSupplierCodeDialogOpen(true);
  };

  // Reverse lookup: When user enters supplier code, find product + packaging
  const lookupBySupplierCode = async (index) => {
    const line = lines[index];
    const supplierCode = line.supplier_product_code?.trim();
    
    // Skip if no customer or no supplier code
    if (!customerId || !supplierCode) return;
    
    // Skip if product is already set (user selected product first)
    if (line.product_id) return;
    
    try {
      const response = await reverseLookupSupplierCode(customerId, supplierCode);
      const data = response.data;
      
      if (data.found) {
        const newLines = [...lines];
        const product = products.find(p => p.id === data.product_id);
        newLines[index] = {
          ...newLines[index],
          product_id: data.product_id,
          product_name: data.product_name || '',
          cobranding_id: data.cobranding_id || '',
          is_fixed_weight: !!product?.is_fixed_weight,
          weight_per_unit: product?.weight_per_unit ?? null,
          units_per_case: product?.units_per_case ?? null,
        };
        setLines(newLines);
        toast.success(`Found: ${data.product_name}${data.cobranding_name ? ` (${data.cobranding_name})` : ''}`);
      }
    } catch (error) {
      // Silent fail - user may be typing a new code
      console.log('Supplier code lookup:', error.message);
    }
  };

  // Handle product selection - auto-fill supplier code AND packaging if mapping exists
  const handleProductSelect = async (index, productId) => {
    const newLines = [...lines];
    const currentLine = newLines[index];
    const product = products.find(p => p.id === productId);
    
    // If supplier code was selected first and is locked, warn and clear it
    if (currentLine.supplier_code_locked && currentLine.supplier_product_code) {
      // The product being selected doesn't match the supplier code's product
      if (currentLine.product_id !== productId) {
        toast.warning('Customer code cleared - product changed');
        newLines[index] = {
          ...currentLine,
          product_id: productId,
          product_name: product?.name || '',
          supplier_product_code: '',
          supplier_code_locked: false,
        };
        setLines(newLines);
        return;
      }
    }
    
    newLines[index] = {
      ...newLines[index],
      product_id: productId,
      product_name: product?.name || '',
      // Carry product-level auto-calc metadata onto the line so the
      // updateLine handler can do weight = qty × weight_per_unit even
      // when no customer-specific conversion formula is configured.
      is_fixed_weight: !!product?.is_fixed_weight,
      weight_per_unit: product?.weight_per_unit ?? null,
      units_per_case: product?.units_per_case ?? null,
    };
    
    // Look for supplier code mapping - first try with current packaging, then product-only
    const currentCobrandingId = newLines[index].cobranding_id;
    const lookupKeyWithPkg = currentCobrandingId ? `${productId}:${currentCobrandingId}` : null;
    
    // Try to find a mapping for this product (with or without packaging)
    let foundMapping = null;
    let foundLookupKey = null;
    
    if (lookupKeyWithPkg && supplierCodeLookup[lookupKeyWithPkg]) {
      // Found match with current packaging
      foundMapping = supplierCodeLookup[lookupKeyWithPkg];
      foundLookupKey = lookupKeyWithPkg;
    } else if (supplierCodeLookup[productId]) {
      // Found match for product without specific packaging
      foundMapping = supplierCodeLookup[productId];
      foundLookupKey = productId;
    } else {
      // Search through all mappings to find any mapping for this product
      for (const [key, mapping] of Object.entries(supplierCodeLookup)) {
        if (key === productId || key.startsWith(`${productId}:`)) {
          foundMapping = mapping;
          foundLookupKey = key;
          break;
        }
      }
    }
    
    if (foundMapping) {
      newLines[index].supplier_product_code = foundMapping.supplier_product_code || foundMapping;
      // Also auto-fill packaging if the mapping has one
      if (foundMapping.cobranding_id) {
        newLines[index].cobranding_id = foundMapping.cobranding_id;
      }
      // Don't lock when product is selected first - user can still change
      newLines[index].supplier_code_locked = false;
    } else {
      newLines[index].supplier_product_code = '';
      newLines[index].supplier_code_locked = false;
    }
    
    setLines(newLines);
    // Lookup conversion formula for this product+customer combo
    lookupLineFormula(index, productId, newLines[index].cobranding_id || '');
  };

  // Handle packaging change - update supplier code if mapping exists for product+packaging combo
  const handlePackagingChange = (index, cobrandingId) => {
    const newLines = [...lines];
    const currentLine = newLines[index];
    const productId = currentLine.product_id;
    
    // If supplier code was selected first and is locked, warn and clear it
    if (currentLine.supplier_code_locked && currentLine.supplier_product_code) {
      // The packaging being selected doesn't match the supplier code's packaging
      if (currentLine.cobranding_id !== cobrandingId) {
        toast.warning('Customer code cleared - packaging changed');
        newLines[index] = {
          ...currentLine,
          cobranding_id: cobrandingId,
          supplier_product_code: '',
          supplier_code_locked: false,
        };
        setLines(newLines);
        return;
      }
    }
    
    newLines[index] = {
      ...newLines[index],
      cobranding_id: cobrandingId,
      supplier_code_locked: false, // Reset lock when packaging is manually changed
    };
    
    // Look for supplier code mapping with this packaging combo
    if (productId && cobrandingId) {
      const lookupKey = `${productId}:${cobrandingId}`;
      if (supplierCodeLookup[lookupKey]) {
        const mapping = supplierCodeLookup[lookupKey];
        newLines[index].supplier_product_code = mapping.supplier_product_code || mapping;
      } else {
        // No mapping for this combo, clear supplier code
        newLines[index].supplier_product_code = '';
      }
    } else if (productId && !cobrandingId) {
      // Packaging cleared (Standard selected), check for product-only mapping
      if (supplierCodeLookup[productId]) {
        const mapping = supplierCodeLookup[productId];
        newLines[index].supplier_product_code = mapping.supplier_product_code || mapping;
      } else {
        // No product-only mapping
        newLines[index].supplier_product_code = '';
      }
    }
    
    setLines(newLines);

    // Re-run conversion-formula lookup with the new packaging combo so the
    // unit/case/weight auto-fill stays in sync (formulas are typically
    // saved per customer + product + cobranding_id).
    if (productId) {
      lookupLineFormula(index, productId, cobrandingId || '');
    }
  };

  // Save new supplier product code mapping
  const handleSaveSupplierCode = async () => {
    if (!newSupplierCode.trim()) {
      toast.error('Please enter a supplier product code');
      return;
    }
    
    const line = lines[supplierCodeLineIndex];
    if (!line || !line.product_id || !customerId) {
      toast.error('Customer and product must be selected');
      return;
    }
    
    setSavingSupplierCode(true);
    try {
      await createCustomerProductMapping({
        customer_id: customerId,
        internal_product_id: line.product_id,
        supplier_product_code: newSupplierCode.trim(),
        cobranding_id: line.cobranding_id || null
      });
      
      // Update local lookup
      const lookupKey = line.cobranding_id ? `${line.product_id}:${line.cobranding_id}` : line.product_id;
      setSupplierCodeLookup(prev => ({
        ...prev,
        [lookupKey]: { supplier_product_code: newSupplierCode.trim(), cobranding_id: line.cobranding_id }
      }));
      
      // Add to supplier code options dropdown
      const cobranding = cobrandings.find(c => c.id === line.cobranding_id);
      const pkgLabel = cobranding ? ` (${cobranding.name})` : '';
      const newOption = {
        value: newSupplierCode.trim(),
        displayValue: newSupplierCode.trim(),
        label: `${newSupplierCode.trim()} - ${line.product_name}${pkgLabel}`,
        product_id: line.product_id,
        product_name: line.product_name,
        cobranding_id: line.cobranding_id || '',
        cobranding_name: cobranding?.name || ''
      };
      setSupplierCodeOptions(prev => [...prev, newOption]);
      
      // Update the line
      const newLines = [...lines];
      newLines[supplierCodeLineIndex].supplier_product_code = newSupplierCode.trim();
      setLines(newLines);
      
      setAddSupplierCodeDialogOpen(false);
      setNewSupplierCode('');
      toast.success('Customer product code saved!');
    } catch (error) {
      console.error('Failed to save supplier code:', error);
      const msg = error.response?.data?.detail || 'Failed to save customer code. Please try again.';
      toast.error(msg);
    } finally {
      setSavingSupplierCode(false);
    }
  };

  // Quick-edit existing customer product code
  const handleOpenEditCode = (lineIndex) => {
    const line = lines[lineIndex];
    if (!line?.supplier_product_code) return;
    // Find the mapping option to get the mapping_id
    const option = supplierCodeOptions.find(o => o.value === line.supplier_product_code);
    if (!option?.mapping_id) {
      toast.error('Cannot edit — mapping not found. Try the Customer Product Codes page.');
      return;
    }
    setEditCodeLineIndex(lineIndex);
    setEditCodeValue(line.supplier_product_code);
    setEditCodeMappingId(option.mapping_id);
    setEditCodeDialogOpen(true);
  };

  const handleSaveEditCode = async () => {
    if (!editCodeValue.trim() || !editCodeMappingId) return;
    setSavingEditCode(true);
    try {
      await updateCustomerProductMapping(editCodeMappingId, {
        supplier_product_code: editCodeValue.trim(),
      });

      const oldCode = lines[editCodeLineIndex].supplier_product_code;

      // Update the line
      const newLines = [...lines];
      newLines[editCodeLineIndex].supplier_product_code = editCodeValue.trim();
      setLines(newLines);

      // Update the option in the dropdown
      setSupplierCodeOptions(prev => prev.map(o =>
        o.mapping_id === editCodeMappingId
          ? { ...o, value: editCodeValue.trim(), displayValue: editCodeValue.trim(), label: o.label.replace(oldCode, editCodeValue.trim()) }
          : o
      ));

      // Update lookup
      const line = lines[editCodeLineIndex];
      const lookupKey = line.cobranding_id ? `${line.product_id}:${line.cobranding_id}` : line.product_id;
      setSupplierCodeLookup(prev => ({
        ...prev,
        [lookupKey]: { ...prev[lookupKey], supplier_product_code: editCodeValue.trim() }
      }));

      setEditCodeDialogOpen(false);
      toast.success('Customer product code updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update code');
    } finally {
      setSavingEditCode(false);
    }
  };

  // Calculate totals - both cumulative and product-wise
  const calculateTotals = () => {
    let totalUnits = 0;
    let totalCases = 0;
    let totalWeight = 0;
    const byProduct = {};
    
    lines.forEach(line => {
      const qty = parseInt(line.quantity) || 0;
      const cases = parseInt(line.cases) || 0;
      const weight = parseFloat(line.estimated_weight) || 0;
      
      totalUnits += qty;
      totalCases += cases;
      totalWeight += weight;
      
      // Group by product
      if (line.product_id && line.product_name) {
        const key = line.product_name;
        if (!byProduct[key]) {
          byProduct[key] = { units: 0, cases: 0, weight: 0 };
        }
        byProduct[key].units += qty;
        byProduct[key].cases += cases;
        byProduct[key].weight += weight;
      }
    });
    
    return { totalUnits, totalCases, totalWeight, byProduct };
  };

  // Create new customer
  const handleCreateCustomer = async () => {
    if (!newCustomerName.trim()) {
      toast.error('Please enter customer name');
      return;
    }
    
    setCreatingCustomer(true);
    try {
      const response = await createCustomer({ name: newCustomerName.trim() });
      const newCustomer = response.data;
      setCustomers([...customers, newCustomer]);
      setCustomerId(newCustomer.id);
      setCustomerData(newCustomer);
      setNewCustomerDialogOpen(false);
      setNewCustomerName('');
      toast.success('Customer created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    } finally {
      setCreatingCustomer(false);
    }
  };

  // Save order with comprehensive validation
  const handleSave = async () => {
    // Validation with clear user messages
    const warnings = [];
    
    if (!customerId) {
      toast.error('Please select a customer before saving');
      return;
    }
    
    if (!dateOrderReceived) {
      toast.error('Please enter the date order was received');
      return;
    }
    
    // Dispatch date warning - don't block, just warn
    if (!dispatchDate) {
      warnings.push('No dispatch date set - order may not appear correctly in the pipeline');
    }
    
    // Validate lines
    const validLines = [];
    const invalidLines = [];
    
    lines.forEach((line, index) => {
      if (!line.product_id) {
        if (line.quantity || line.notes) {
          invalidLines.push(`Line ${index + 1}: Missing product selection`);
        }
        return; // Skip empty lines
      }
      
      if (!line.quantity || parseInt(line.quantity) <= 0) {
        invalidLines.push(`Line ${index + 1} (${line.product_name || 'Unknown'}): Quantity must be greater than 0`);
        return;
      }
      
      validLines.push(line);
    });
    
    if (invalidLines.length > 0) {
      toast.error(
        <div>
          <p className="font-semibold mb-2">Please fix the following:</p>
          <ul className="list-disc list-inside text-sm">
            {invalidLines.map((msg, i) => <li key={i}>{msg}</li>)}
          </ul>
        </div>,
        { duration: 6000 }
      );
      return;
    }
    
    if (validLines.length === 0) {
      toast.error('Please add at least one product with quantity');
      return;
    }
    
    // Show warnings but proceed
    if (warnings.length > 0) {
      toast.warning(
        <div>
          <p className="font-semibold mb-1">Note:</p>
          <ul className="list-disc list-inside text-sm">
            {warnings.map((msg, i) => <li key={i}>{msg}</li>)}
          </ul>
        </div>,
        { duration: 4000 }
      );
    }
    
    setSaving(true);
    try {
      // Build delivery address string from selected address
      let deliveryAddress = '';
      if (selectedAddress) {
        const addressParts = [
          selectedAddress.address_line1,
          selectedAddress.address_line2,
          selectedAddress.city,
          selectedAddress.county,
          selectedAddress.postcode,
          selectedAddress.country
        ].filter(Boolean);
        deliveryAddress = addressParts.join(', ');
      }
      
      const orderData = {
        customer_id: customerId,
        status: isEdit ? status : 'draft',
        date_order_received: dateOrderReceived || null,
        dispatch_date: dispatchDate || null,
        customer_delivery_date: customerDeliveryDate || null,
        po_number: poNumber || null,
        po_date: poDate || null,
        vehicle_info: transportAgent || null,
        num_pallets: numPallets ? parseInt(numPallets) : null,
        cobranding_id: cobrandingId && cobrandingId !== 'none' ? cobrandingId : null,
        notes: notes || null,
        delivery_address_id: selectedAddressId || 'primary',
        delivery_address: deliveryAddress || null,
        selected_delivery_address: selectedAddress ? {
          id: selectedAddress.id,
          label: selectedAddress.label,
          alias_name: selectedAddress.alias_name || null,
          address_line1: selectedAddress.address_line1 || null,
          address_line2: selectedAddress.address_line2 || null,
          city: selectedAddress.city || null,
          county: selectedAddress.county || null,
          postcode: selectedAddress.postcode || null,
          country: selectedAddress.country || null,
          contact_name: selectedAddress.contact_name || null,
          contact_phone: selectedAddress.contact_phone || null,
          delivery_instructions: selectedAddress.delivery_instructions || null,
          is_primary: !!selectedAddress.is_primary,
        } : null,
        delivery_contact_name: selectedAddress?.contact_name || null,
        delivery_contact_phone: selectedAddress?.contact_phone || null,
        delivery_instructions: selectedAddress?.delivery_instructions || null,
        acknowledge_duplicate_po: acknowledgeDuplicatePo,
        lines: validLines.map(line => {
          let weight = parseFloat(line.estimated_weight) || null;
          // Convert lbs → kg if lbs mode is active
          if (weight && weightInLbs) {
            weight = Math.round(weight * 0.45359237 * 100) / 100;
          }
          return {
            product_id: line.product_id,
            product_name: line.product_name,
            cobranding_id: line.cobranding_id || null,
            quantity: parseInt(line.quantity),
            cases: parseInt(line.cases) || null,
            estimated_weight: weight,
            notes: line.notes || null,
            supplier_product_code: line.supplier_product_code || null,
          };
        }),
      };
      
      if (isEdit) {
        await updateDispatch(id, orderData);
        toast.success('Sales order updated');
        // Save any learned formulas
        saveLearnedFormulas();
      } else {
        const response = await createDispatch(orderData);
        const createdNumber = response.data.dispatch_number;
        
        // Save any learned formulas
        saveLearnedFormulas();
        
        // Show the success popup with the order number
        setSuccessPopup({ number: createdNumber, phased: response.data.phased });
        
        // Auto-dismiss after 5 seconds and reset form for next order
        setTimeout(() => {
          setSuccessPopup(null);
          resetFormForNewOrder();
        }, 5000);
      }
      // Reset the acknowledgement so subsequent saves re-run the check
      setAcknowledgeDuplicatePo(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      // ── Duplicate-PO short-circuit ──
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'DUPLICATE_PO_NUMBER') {
        setDuplicatePoExisting(detail.existing_order);
        setDuplicatePoModalOpen(true);
        return;
      }
      if (detail && typeof detail === 'string') {
        toast.error(detail);
      } else if (detail && Array.isArray(detail)) {
        toast.error(
          <div>
            <p className="font-semibold mb-2">Validation errors:</p>
            <ul className="list-disc list-inside text-sm">
              {detail.map((err, i) => <li key={i}>{err.msg || err}</li>)}
            </ul>
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.error('Failed to save order. Please check all fields and try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Fields become read-only for non-draft orders or if user lacks edit permission
  const isReadOnly = (isEdit && status !== 'draft') || !canEdit(user?.role);
  const totals = calculateTotals();

  // Reset form for the next new order — keeps reference data loaded
  const resetFormForNewOrder = () => {
    setCustomerId('');
    setCustomerData(null);
    setCustomerAddresses([]);
    setSelectedAddressId('primary');
    setSelectedAddress(null);
    setCobrandingId('');
    setPoNumber('');
    setPoDate('');
    setDateOrderReceived(getTodayFormatted());
    setDispatchDate('');
    setCustomerDeliveryDate('');
    setTransportAgent('');
    setNumPallets('');
    setNotes('');
    setOrderNumber('');
    setStatus('draft');
    setLines([createEmptyLine()]);
    setSupplierCodeLookup({});
    setSupplierCodeOptions([]);
    setMoqAlert(null);
    // Refresh next number preview
    getNextDispatchNumber()
      .then(res => setNextNumber(res.data.next_number))
      .catch(() => {});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-6xl mx-auto">
      {/* Next Order Number Banner (new orders only) */}
      {!isEdit && nextNumber && (
        <div className="flex items-center justify-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20" data-testid="next-order-number-banner">
          <Hash className="w-5 h-5 text-primary" />
          <span className="text-sm text-muted-foreground">Next Order Number:</span>
          <span className="text-2xl font-bold tracking-wider text-primary" data-testid="next-order-number-value">
            {nextNumberLoading ? '...' : nextNumber}
          </span>
          <span className="text-xs text-muted-foreground">(confirmed on submission)</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/planner')} data-testid="btn-back">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold">
                {isEdit ? `Edit Sales Order ${orderNumber}` : 'New Sales Order'}
              </h1>
              {isEdit && (
                <Badge className="bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-100" data-testid="badge-editing-sales-order">
                  Editing Sales Order
                </Badge>
              )}
              {isEdit && status && (
                <Badge variant="outline" className="capitalize" data-testid="badge-order-status">
                  {String(status).replace(/_/g, ' ')}
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground text-sm">
              {isEdit ? 'Modify order details and line items. Saving will update this existing order.' : 'Enter customer order details and products'}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          {!isReadOnly && (
            <>
              <Button variant="outline" onClick={() => navigate('/planner')} data-testid="btn-cancel-order">
                Exit Without Saving
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="btn-save-order">
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" /> Save Order</>
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Customer & Order Details - Compact Layout */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">Order Details</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Row 1: Customer + PO + Dates */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {/* Customer Selection */}
            <div className="space-y-1 col-span-2">
              <Label className="text-xs">Customer *</Label>
              <div className="flex gap-1">
                <div className="flex-1 min-w-0">
                  <SearchableSelect
                    options={customers.map(c => ({ 
                      value: c.id, 
                      label: c.delivery_address_line1 || c.delivery_city ? c.name : `⚠️ ${c.name} (no address)`
                    }))}
                    value={customerId}
                    onValueChange={handleCustomerChange}
                    placeholder="Select customer..."
                    disabled={isReadOnly}
                    data-testid="select-customer"
                  />
                </div>
                {!isReadOnly && (
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setNewCustomerDialogOpen(true)}
                    title="Add new customer"
                    data-testid="btn-new-customer"
                    className="flex-shrink-0 h-9 w-9"
                  >
                    <UserPlus className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            
            {/* PO Number */}
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-2">
                <span>PO Number</span>
                {inlinePoStatus === 'checking' && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] text-zinc-500"
                    data-testid="po-inline-status-checking"
                  >
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Checking…
                  </span>
                )}
                {inlinePoStatus === 'available' && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium"
                    data-testid="po-inline-status-available"
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    Available
                  </span>
                )}
                {inlinePoStatus === 'duplicate' && inlineDuplicateOrder && (
                  <button
                    type="button"
                    onClick={() => {
                      if (isEdit && inlineDuplicateOrder.id === id) return;
                      setDuplicatePoExisting(inlineDuplicateOrder);
                      setDuplicatePoModalOpen(true);
                    }}
                    className="inline-flex items-center gap-1 text-[10px] text-amber-700 font-medium hover:underline"
                    title={`Already used on order ${inlineDuplicateOrder.dispatch_number || inlineDuplicateOrder.order_reference || ''}`}
                    data-testid="po-inline-status-duplicate"
                  >
                    <AlertTriangle className="w-3 h-3" />
                    Already used on {inlineDuplicateOrder.dispatch_number || inlineDuplicateOrder.order_reference || 'an existing order'}
                  </button>
                )}
              </Label>
              <Input
                value={poNumber}
                onChange={(e) => { setPoNumber(e.target.value); setAcknowledgeDuplicatePo(false); }}
                onBlur={handleBlurPoNumber}
                placeholder="PO ref"
                disabled={isReadOnly}
                data-testid="input-po-number"
                className={`h-9 ${inlinePoStatus === 'duplicate' ? 'border-amber-500 focus-visible:ring-amber-400' : ''}`}
                aria-invalid={inlinePoStatus === 'duplicate' ? 'true' : 'false'}
              />
            </div>
            
            {/* PO Date */}
            <div className="space-y-1">
              <Label className="text-xs">PO Date</Label>
              <DatePicker
                value={poDate}
                onChange={(date) => {
                  setPoDate(date);
                  if (date && !isReadOnly) {
                    setDateOrderReceived(date);
                  }
                }}
                disabled={isReadOnly}
                data-testid="input-po-date"
              />
            </div>
            
            {/* Date Order Received */}
            <div className="space-y-1">
              <Label className="text-xs">Date Received *</Label>
              <DatePicker
                value={dateOrderReceived}
                onChange={setDateOrderReceived}
                disabled={isReadOnly}
                data-testid="input-date-received"
              />
            </div>
            
            {/* Customer Requested Delivery Date */}
            <div className="space-y-1">
              <Label className="text-xs">Delivery Date</Label>
              <DatePicker
                value={customerDeliveryDate}
                onChange={setCustomerDeliveryDate}
                placeholder="Customer req"
                disabled={isReadOnly}
                data-testid="input-customer-delivery"
              />
            </div>
          </div>
          
          {/* Row 2: Dispatch + Transport + Pallets + Delivery Address + Notes */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-3">
            {/* Dispatch/Collection Date - REQUIRED */}
            <div className="space-y-1">
              <Label className={`text-xs ${!dispatchDate && !isReadOnly ? 'text-amber-700 font-semibold' : ''}`}>Dispatch Date <span className="text-red-500">*</span></Label>
              <DatePicker
                value={dispatchDate}
                onChange={setDispatchDate}
                placeholder="Uplift date"
                disabled={isReadOnly}
                data-testid="input-dispatch-date"
                className={!dispatchDate && !isReadOnly ? "border-amber-400 bg-amber-50/60 ring-1 ring-amber-300" : ""}
              />
            </div>
            
            {/* Transport */}
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1">
                <Truck className="w-3 h-3" />
                Transport
              </Label>
              <Select
                value={transportAgent}
                onValueChange={setTransportAgent}
                disabled={isReadOnly}
              >
                <SelectTrigger data-testid="select-transport" className="h-9">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {transportOptions.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Number of Pallets */}
            <div className="space-y-1">
              <Label className="text-xs">Pallets</Label>
              <Input
                type="number"
                value={numPallets}
                onChange={(e) => setNumPallets(e.target.value)}
                placeholder="0"
                disabled={isReadOnly}
                data-testid="input-pallets"
                min="0"
                className="h-9"
              />
            </div>

            {/* Delivery Address Dropdown - inline */}
            {customerData && customerAddresses.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  Deliver To
                </Label>
                {customerAddresses.length === 1 ? (
                  <div className="text-xs h-9 flex items-center px-3 bg-muted/30 rounded border truncate" data-testid="delivery-address-display">
                    {[selectedAddress?.address_line1, selectedAddress?.city, selectedAddress?.postcode].filter(v => v && String(v).trim().toLowerCase() !== 'nan').join(' • ') || customerData.name}
                  </div>
                ) : (
                  <Select
                    value={selectedAddressId}
                    onValueChange={handleAddressChange}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger data-testid="select-delivery-address" className="h-9">
                      <SelectValue placeholder="Select delivery address" />
                    </SelectTrigger>
                    <SelectContent>
                      {customerAddresses.map(addr => (
                        <SelectItem key={addr.id} value={addr.id}>
                          <span className="flex items-center gap-2">
                            {addr.label}
                            {addr.is_default && (
                              <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">Default</span>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
            
            {/* Notes */}
            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Special instructions..."
                disabled={isReadOnly}
                data-testid="input-notes"
                className="h-9"
              />
            </div>
          </div>

          {/* Selected address detail - compact line below */}
          {customerData && selectedAddress && customerAddresses.length > 1 && (
            <div className="mt-1.5 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 pl-1" data-testid="delivery-address-detail">
              {selectedAddress.address_line1 && <span>{selectedAddress.address_line1}</span>}
              {selectedAddress.address_line2 && <span>{selectedAddress.address_line2}</span>}
              {selectedAddress.city && <span>{selectedAddress.city}</span>}
              {selectedAddress.postcode && <span>{selectedAddress.postcode}</span>}
              {selectedAddress.contact_name && <span className="text-zinc-500">Contact: {selectedAddress.contact_name}</span>}
              {selectedAddress.delivery_instructions && <span className="text-amber-600">Note: {selectedAddress.delivery_instructions}</span>}
            </div>
          )}
          
          {/* Warning for customers with no address - only show when relevant */}
          {customerData && !customerData.delivery_address_line1 && !customerData.delivery_city && (
            <div className="flex items-center gap-2 mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>No delivery address. Add address in Customer Management before dispatching.</span>
            </div>
          )}

          {/* MOQ Alert Banner — non-blocking info flash */}
          {moqAlert && (
            <div className="flex items-start gap-3 mt-3 p-3 bg-orange-50 border border-orange-300 rounded-lg animate-in fade-in slide-in-from-top-2" data-testid="moq-alert-banner">
              <Package className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-orange-800">Minimum Order Requirement — {moqAlert.customerName}</p>
                <p className="text-sm text-orange-700 mt-0.5">{moqAlert.text}</p>
              </div>
              <button onClick={() => setMoqAlert(null)} className="text-orange-400 hover:text-orange-600 p-0.5" data-testid="moq-alert-dismiss">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Lines */}
      <OrderLineItemsTable
        lines={lines}
        products={products}
        cobrandings={cobrandings}
        supplierCodeOptions={supplierCodeOptions}
        customerId={customerId}
        isReadOnly={isReadOnly}
        totals={totals}
        weightInLbs={weightInLbs}
        onToggleWeightUnit={() => setWeightInLbs(prev => !prev)}
        onAddLine={addLine}
        onRemoveLine={removeLine}
        onUpdateLine={updateLine}
        onSupplierCodeSelect={handleSupplierCodeSelect}
        onCreateNewSupplierCode={handleCreateNewSupplierCode}
        onEditSupplierCode={handleOpenEditCode}
        onProductSelect={handleProductSelect}
        onPackagingChange={handlePackagingChange}
        onFormulaOverrideToggle={(index, override) => {
          const newLines = [...lines];
          newLines[index] = { ...newLines[index], formulaOverride: override };
          setLines(newLines);
        }}
      />

      {!isEdit && !isReadOnly && <SeasonalSplitNotice lines={lines} lookup={supplierCodeLookup} />}

      {/* Bottom Action Buttons */}
      {!isReadOnly && (
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => navigate('/planner')} data-testid="btn-cancel-order-bottom">
            Exit Without Saving
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="btn-save-order-bottom">
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Submit Order</>
            )}
          </Button>
        </div>
      )}

      {/* New Customer Dialog */}
      <NewCustomerDialog
        open={newCustomerDialogOpen}
        onOpenChange={setNewCustomerDialogOpen}
        customerName={newCustomerName}
        setCustomerName={setNewCustomerName}
        creating={creatingCustomer}
        onCreateCustomer={handleCreateCustomer}
      />

      {/* Duplicate PO warning */}
      <DuplicatePoWarningModal
        open={duplicatePoModalOpen}
        onOpenChange={setDuplicatePoModalOpen}
        existingOrder={duplicatePoExisting}
        poNumber={poNumber}
        isEditingCurrent={isEdit && duplicatePoExisting?.id === id}
        onChangePoNumber={() => {
          setDuplicatePoExisting(null);
          // Focus the PO number input on the next tick
          setTimeout(() => {
            const el = document.querySelector('[data-testid="input-po-number"]');
            el?.focus?.();
            el?.select?.();
          }, 50);
        }}
        onProceedAnyway={() => {
          setDuplicatePoModalOpen(false);
          setAcknowledgeDuplicatePo(true);
          // Re-run save with the acknowledgement flag — the next save call
          // will read acknowledgeDuplicatePo=true and the server will accept.
          setTimeout(() => handleSave(), 100);
        }}
      />

      {/* Add Customer Product Code Dialog */}
      <SupplierCodeDialog
        open={addSupplierCodeDialogOpen}
        onOpenChange={setAddSupplierCodeDialogOpen}
        customerData={customerData}
        lines={lines}
        lineIndex={supplierCodeLineIndex}
        products={products}
        cobrandings={cobrandings}
        supplierCode={newSupplierCode}
        setSupplierCode={setNewSupplierCode}
        saving={savingSupplierCode}
        onSave={handleSaveSupplierCode}
        onUpdateLine={(idx, field, value) => {
          setLines(prev => {
            const newLines = [...prev];
            if (idx !== null && newLines[idx]) {
              newLines[idx] = { ...newLines[idx], [field]: value };
            }
            return newLines;
          });
        }}
      />

      {/* Quick-Edit Customer Product Code Dialog */}
      <Dialog open={editCodeDialogOpen} onOpenChange={setEditCodeDialogOpen}>
        <DialogContent className="sm:max-w-[400px]" data-testid="edit-customer-code-dialog" aria-describedby="edit-code-desc">
          <DialogHeader>
            <DialogTitle>Edit Customer Product Code</DialogTitle>
          </DialogHeader>
          <div className="py-3" id="edit-code-desc">
            {editCodeLineIndex !== null && lines[editCodeLineIndex] && (
              <p className="text-sm text-muted-foreground mb-3">
                Product: <span className="font-medium text-foreground">{lines[editCodeLineIndex].product_name}</span>
              </p>
            )}
            <Label htmlFor="editCode">Customer Code</Label>
            <Input
              id="editCode"
              value={editCodeValue}
              onChange={(e) => setEditCodeValue(e.target.value)}
              placeholder="Enter new code"
              className="mt-1"
              data-testid="input-edit-customer-code"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && editCodeValue.trim()) handleSaveEditCode(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCodeDialogOpen(false)} data-testid="btn-cancel-edit-code">Cancel</Button>
            <Button
              onClick={handleSaveEditCode}
              disabled={!editCodeValue.trim() || savingEditCode}
              data-testid="btn-save-edit-code"
            >
              {savingEditCode ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {successPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="order-success-popup">
          <div className="relative bg-white rounded-2xl shadow-2xl p-8 sm:p-12 max-w-md w-full mx-4 text-center animate-in zoom-in-95 duration-300">
            <button
              onClick={() => { setSuccessPopup(null); resetFormForNewOrder(); }}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-muted transition-colors"
              data-testid="order-success-close"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
            </div>
            <h2 className="text-lg font-medium text-muted-foreground mb-2">Sales Order Created</h2>
            <p className="text-5xl sm:text-6xl font-black tracking-wider text-primary mb-4" data-testid="order-success-number">
              {successPopup.number}
            </p>
            <SplitCreatedNote phased={successPopup.phased} />
            <p className="text-sm text-muted-foreground mb-6">Order saved — ready for next entry</p>
            <div className="w-full bg-muted rounded-full h-1 overflow-hidden">
              <div className="bg-primary h-1 rounded-full animate-[shrink_5s_linear_forwards]" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
